import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { zonedTimeToUtc } from 'date-fns-tz';
import { useSelector } from 'react-redux';
import { SingleValue } from 'react-select';
import { useAppDispatch } from 'app/store';
import TimeZoneDifferenceBanner from 'components/timeOff/TimeZoneDifferenceBanner';
import { ISelectOption } from 'components/shared/forms/DropdownSelect/DropdownSelect';
import CustomDatePicker from 'components/shared/forms/datePickers/CustomDatePicker';
import CustomTimePicker from 'components/shared/forms/timePickers/CustomTimePicker';
import DropdownSelect from 'components/shared/forms/DropdownSelect';
import CustomCheckbox from 'components/shared/forms/CustomCheckbox';
import TextArea from 'components/shared/forms/textAreas/TextArea';
import Button from 'components/shared/Button';
import { MaximumLargeCommentLengthValidationError } from 'constants/formsConstants';
import { isLoadingTimeEntriesSelector } from 'features/timeEntries/timeEntriesSlice';
import { selectInfoUserSelector } from 'features/user/userSlice';
import { organizationInfoSelector } from 'features/organization/organizationSlice';
import { AddBulkTimeFormState } from 'features/timesheets/timesheetsTypes';
import {addBulkTimeEntry, addTimeEntry} from 'features/timeEntries/timeEntriesActions';
import {memberInfoTimesheetsSelector, setAddBulkTimeFormState,} from 'features/timesheets/timesheetsSlice';
import { checkIsNextDay } from 'helpers/checkIsNextDay';
import { setTimeInDate } from 'helpers/setTimeInDate';
import { checkIsFuture } from 'helpers/checkIsFuture';
import { checkIsToday } from 'helpers/checkIsToday';
import styles from './AddBulkTimeForm.module.scss';
import {anyJiraConnections, generateJiraIssueDescription} from "features/jira/jiraActions";
import {anyJiraConnectionsSelector} from "features/jira/jiraSlice";
import {IGenerateJiraIssuesDescription} from "../../../features/jira/jiraTypes";
import ActionsPopover from 'components/shared/ActionsPopover';
import CustomInput from "../../shared/forms/CustomInput";
import CustomNumberInput from "../../shared/forms/CustomNumberInput";
import WeekdayToggle from 'components/shared/WeekdayToggle/WeekdayToggle';
import { weekDays } from 'components/shared/WeekdayToggle/weekdays';
import { PulseLoader } from 'react-spinners';

interface AddBulkTimeFormNewProps {
  data: AddBulkTimeFormState;
  handleClose: () => void;
}

const AddBulkTimeForm: FC<AddBulkTimeFormNewProps> = ({ data, handleClose }) => {
  const jiraLinkPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/\S*)?$/;
  const MAX_REPEAT_WEEKS = 5;
  
  const isTimeEntriesLoading = useSelector(isLoadingTimeEntriesSelector);
  const selectedMemberInfo = useSelector(memberInfoTimesheetsSelector);
  const userInfo = useSelector(selectInfoUserSelector);
  const organizationInfo = useSelector(organizationInfoSelector);  
  const jiraConnectionsExist = useSelector(anyJiraConnectionsSelector);
  const dispatch = useAppDispatch();
  const [isNextDay, setIsNextDay] = useState(false);
  const [isFuture, setIsFuture] = useState(false);
  const [isProjectFieldTouched, setIsProjectFieldTouched] = useState(false);
  const [jiraIssueLinks, setJiraIssueLinks] = useState(['']);
  const [jiraIssueLinkErrors, setJiraIssueLinkErrors] = useState([false]);
  const [isJiraLoading, setIsJiraLoading] = useState(false);

  const [hasSelectedDates, setHasSelectedDates] = useState(false);
  const [bulkStartDate, setBulkStartDate] = useState(new Date());
  const [bulkEndDate, setBulkEndDate] = useState(new Date());
  const [repeatWeeksInterval, setRepeatWeeksInterval] = useState(1);
  
  const [selectedWeekDays, setSelectedWeekDays] = useState<Record<number, boolean>>({
    0: false, // Sunday
    1: true, // Monday
    2: true, // Tuesday
    3: true, // Wednesday
    4: true, // Thursday
    5: true, // Friday
    6: false, // Saturday
  });

  const isJiraAvailable = organizationInfo.permissions?.some(
    (el) => el.name === 'AccessJiraTicketBasedTimeEntryCommentGeneration'
  );
  
  const handleAddJiraIssueLink = () => {
      if (jiraIssueLinks.length < 3) {
          setJiraIssueLinks([...jiraIssueLinks, ""]);
      }
  };
  
  const handleJiraIssueLinkChange = (index: number, value: string) => {
      const newJiraIssueLinks = [...jiraIssueLinks];
      newJiraIssueLinks[index] = value;
      setJiraIssueLinks(newJiraIssueLinks);

      const updatedErrors = [...jiraIssueLinkErrors];
      updatedErrors[index] = value !== '' && !jiraLinkPattern.test(value);
      setJiraIssueLinkErrors(updatedErrors);
  };

  const isJiraGenerateButtonEnabled =
      jiraIssueLinks.some((link) => link.trim() !== '') &&
      jiraIssueLinkErrors.every((error) => !error);
  
  const isToday = checkIsToday(
    data.timeTrackingDates,
    data.selectedTimeZoneName
  );

  const handleProjectChange = (project: SingleValue<ISelectOption>) => {
    dispatch(
      setAddBulkTimeFormState({
        ...data,
        project,
        isProjectSelected: true,
      })
    );
  };

  const handleBulkStartDateChange = (date: Date) => {
    setBulkStartDate(date);
    if (date > bulkEndDate) {
      setBulkEndDate(date);
    }
  };

  const handleBulkEndDateChange = (date: Date) => {
    setBulkEndDate(date);
    if (date < bulkStartDate) {
      setBulkStartDate(date);
    }
  };

  const handleRepeatWeeksChange = (weeks: number) => {
    setRepeatWeeksInterval(Math.min(weeks, MAX_REPEAT_WEEKS));
  };

  const handleWeekDayToggle = (dayIndex: number) => {
    setSelectedWeekDays(prev => ({
      ...prev,
      [dayIndex]: !prev[dayIndex]
    }));
  };

  const updateSelectedDates = () => {
    const selectedDates: Date[] = [];
    
    const start = new Date(bulkStartDate);
    const end = new Date(bulkEndDate);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    let weekCounter = 0;
    let shouldContinue = true;

    while (shouldContinue) {
      const weekStart = new Date(start);
      weekStart.setDate(weekStart.getDate() + weekCounter * 7 * repeatWeeksInterval);

      if (weekStart > end) {
        shouldContinue = false;
      } else {
        for (let day = 0; day < 7; day++) {
          const date = new Date(weekStart);
          date.setDate(date.getDate() + day);

          if (date >= start && date <= end && selectedWeekDays[date.getDay()]) {
            selectedDates.push(new Date(date));
          }
        }
        weekCounter++;
      }
    }
    
    const hasDates = selectedDates.length > 0;
    setHasSelectedDates(hasDates);

    const latestDate = hasDates
      ? new Date(Math.max(...selectedDates.map(d => d.getTime())))
      : null;

    dispatch(
      setAddBulkTimeFormState({
        ...data,
        timeTrackingDates: selectedDates,
        startTime: latestDate != null
          ? setTimeInDate(latestDate, data.startTime.getHours(), data.startTime.getMinutes()) 
          : data.startTime,
        endTime: latestDate != null
          ? setTimeInDate(latestDate, isNextDay ? data.endTime.getHours() + 24 : data.endTime.getHours(), data.endTime.getMinutes())
          : data.endTime,
      })
    );
  }

  useEffect(() => {
    updateSelectedDates();
  }, [selectedWeekDays, bulkStartDate, bulkEndDate, repeatWeeksInterval]);

  const handleStartTimeChange = (inputStartTime: Date) => {
    const latestDate = data.timeTrackingDates.length > 0
        ? new Date(Math.max(...data.timeTrackingDates.map(date => date.getTime())))
        : null;
    if(latestDate == null) return;
      
    if (
      // IS NEXT DAY BY TIME COMPARISON
      inputStartTime.getHours() > data.endTime.getHours() ||
      (inputStartTime.getHours() === data.endTime.getHours() &&
        inputStartTime.getMinutes() >= data.endTime.getMinutes())
    ) {
      dispatch(
        setAddBulkTimeFormState({
          ...data,
          startTime: setTimeInDate(
            latestDate,
            inputStartTime.getHours(),
            inputStartTime.getMinutes()
          ),
          endTime: setTimeInDate(
            latestDate,
            data.endTime.getHours() + 24,
            data.endTime.getMinutes()
          ),
        })
      );
    } else {
      dispatch(
        setAddBulkTimeFormState({
          ...data,
          startTime: setTimeInDate(
            latestDate,
            inputStartTime.getHours(),
            inputStartTime.getMinutes()
          ),
          endTime: setTimeInDate(
            latestDate,
            data.endTime.getHours(),
            data.endTime.getMinutes()
          ),
        })
      );
    }
  };
  const handleEndTimeChange = (inputEndTime: Date) => {
    const latestDate = data.timeTrackingDates.length > 0
      ? new Date(Math.max(...data.timeTrackingDates.map(date => date.getTime())))
      : null;
    if(latestDate == null) return;
      
    if (
      // IS NEXT DAY BY TIME COMPARISON
      inputEndTime.getHours() < data.startTime.getHours() ||
      (inputEndTime.getHours() === data.startTime.getHours() &&
        inputEndTime.getMinutes() <= data.startTime.getMinutes())
    ) {
      dispatch(
        setAddBulkTimeFormState({
          ...data,
          endTime: setTimeInDate(
            latestDate,
            inputEndTime.getHours() + 24,
            inputEndTime.getMinutes()
          ),
        })
      );
    } else {
      dispatch(
        setAddBulkTimeFormState({
          ...data,
          endTime: setTimeInDate(
            latestDate,
            inputEndTime.getHours(),
            inputEndTime.getMinutes()
          ),
        })
      );
    }
  };
  const toggleBillable = () => {
    dispatch(setAddBulkTimeFormState({ ...data, isBillable: !data.isBillable }));
  };
  const handleNotesChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(
      setAddBulkTimeFormState({
        ...data,
        notes: e.target.value,
        commentsLengthError:
          e.target.value.length > MaximumLargeCommentLengthValidationError,
      })
    );
  };
  const handleSubmit = () => {
    dispatch(
      addBulkTimeEntry({
        memberId: Number(selectedMemberInfo.id),
        projectId: Number(data.project?.value),
        dates: data.timeTrackingDates.map(date => {
          const startDateTime = new Date(date);
          startDateTime.setHours(data.startTime.getHours(), data.startTime.getMinutes());
          
          const endDateTime = new Date(date);
          endDateTime.setHours(data.endTime.getHours(), data.endTime.getMinutes());
          
          if (endDateTime < startDateTime) {
            endDateTime.setDate(endDateTime.getDate() + 1);
          }

          return {
            start: zonedTimeToUtc(startDateTime, data.selectedTimeZoneName),
            end: zonedTimeToUtc(endDateTime, data.selectedTimeZoneName)
          };
        }),
        isBillable: data.isBillable,
        note: data.notes,
      })
    );

    handleClose();
  };
  
  const handleGenerateJiraIssueSummary = () => {
      if (jiraIssueLinks) {
          setIsJiraLoading(true);
          const request: IGenerateJiraIssuesDescription = {
              urls: jiraIssueLinks,
          }
          dispatch(generateJiraIssueDescription(request))
              .then(result => {
                  console.log(result);
                  const payload = result.payload ?? "";
                  dispatch(
                          setAddBulkTimeFormState({
                              ...data,
                              notes: payload,
                              commentsLengthError: payload.length > MaximumLargeCommentLengthValidationError,
                          })
                      );
              })
              .finally(() => {
                  setJiraIssueLinks(['']);
                  setIsJiraLoading(false);
              });
      }
  }
  
  const projectsOptions = selectedMemberInfo.projects.map((p) => ({
    value: String(p.id),
    label: p.name,
  }));
  const disableSubmitCondition =
    isFuture ||
    !hasSelectedDates ||
    !data.isProjectSelected ||
    isTimeEntriesLoading ||
    data.commentsLengthError;

  useEffect(() => {
    setIsNextDay(checkIsNextDay(data.startTime, data.endTime));
  }, [data.startTime, data.endTime]);

  useEffect(() => {
    setIsFuture(checkIsFuture(data.endTime, data.selectedTimeZoneName));
  }, [data.endTime, data.selectedTimeZoneName]);

  useEffect(() => {
    if (selectedMemberInfo.projects.length === 1) {
      dispatch(
        setAddBulkTimeFormState({
          ...data,
          project: {
            label: selectedMemberInfo.projects[0].name,
            value: String(selectedMemberInfo.projects[0].id),
          },
          isProjectSelected: true,
        })
      );
    }
    if(isJiraAvailable) {
      dispatch(anyJiraConnections());
    }
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.memberName}>{selectedMemberInfo.name}</div>
      <DropdownSelect
        label="Project*"
        value={data.project}
        options={projectsOptions}
        onChange={handleProjectChange}
        isSearchable={false}
        blurInputOnSelect
        placeholder="Select a project"
        error={!data.isProjectSelected && isProjectFieldTouched}
        errorMessage="*Project should be selected"
        onBlur={() => setIsProjectFieldTouched(true)}
      />
      {isJiraAvailable && jiraConnectionsExist &&
        jiraIssueLinks.map((value, index) => (
          <div className={styles.jiraWrapper} key={index}>
            <CustomInput
              label={index === 0 ? "Jira Issue" : ""}
              value={jiraIssueLinks[index]}
              placeholder={"https://example/browse/A-" + (index + 1)}
              onChange={(e) => handleJiraIssueLinkChange(index, e.target.value)}
              error={jiraIssueLinkErrors[index]}
              errorMessage={"Invalid format. Please enter a valid URL"}
            />
            {index === jiraIssueLinks.length - 1 && 
              <div className={styles.jiraActionWrapper}>
                <ActionsPopover>
                  <li onClick={isJiraGenerateButtonEnabled ? handleGenerateJiraIssueSummary : undefined}>
                    <button aria-label={'Generate description'} disabled={!isJiraGenerateButtonEnabled} className={styles.jiraActionButtonWrapper}>Generate</button>
                  </li>
                  <li onClick={jiraIssueLinks.length < 3 ? handleAddJiraIssueLink : undefined}>
                    <button aria-label={'Add line for url'} disabled={jiraIssueLinks.length >= 3} className={styles.jiraActionButtonWrapper}>Add line</button>
                  </li>
                </ActionsPopover>
              </div>
            }
          </div>
        ))
      }      
      <div className={styles.trackerWrapper}>
        <div className={styles.dateWrapper}>
          <CustomDatePicker
            now={bulkEndDate}
            selected={bulkStartDate}
            onChange={handleBulkStartDateChange}
            label={"Start date"}
            calendarStartDay={userInfo?.firstDayOfWeek ?? 1}
          />
        </div>
        <div className={styles.dateWrapper}>
          <CustomDatePicker
            now={data.calendarNow}
            selected={bulkEndDate}
            onChange={handleBulkEndDateChange}
            label={"End date"}
            calendarStartDay={userInfo?.firstDayOfWeek ?? 1}
          />
        </div>
        <div className={styles.dateWrapper}>
          <CustomNumberInput
            onChange={e => handleRepeatWeeksChange(Number(e.target.value))}
            min={1}
            value={repeatWeeksInterval}
            max={MAX_REPEAT_WEEKS}
            label={"Repeat weeks interval"}
          />
        </div>
        <div className={styles.daysOfWeekWrapper}>
          {weekDays.map((day, index) => (
              <WeekdayToggle
                key={index}
                dayName={day}
                isActive={selectedWeekDays[index]}
                onClick={() => handleWeekDayToggle(index)}
              />
          ))}
        </div>
      </div>

      <div className={styles.trackerWrapper}>
        <div className={styles.timeWrapper}>
          <CustomTimePicker
            label="From"
            now={data.calendarNow}
            selected={data.startTime}
            onChange={handleStartTimeChange}
            isToday={isToday}
            disabled={!hasSelectedDates}
          />
          <CustomTimePicker
            label="To"
            now={data.calendarNow}
            selected={data.endTime}
            onChange={handleEndTimeChange}
            isToday={isToday}
            isNextDay={isNextDay}
            isFuture={isFuture || (isToday && isNextDay)}
            disabled={!hasSelectedDates}
          />
        </div>
        {!hasSelectedDates && (
          <p className={styles.hintText}>
            Please select weekdays that fall within the date range
          </p>
        )}
      </div>

      <TimeZoneDifferenceBanner
        startTime={zonedTimeToUtc(data.startTime, data.selectedTimeZoneName)}
        endTime={zonedTimeToUtc(data.endTime, data.selectedTimeZoneName)}
        memberTimeZone={selectedMemberInfo.timeZone}
        selectedInTimeZoneFilterTimeZone={data.selectedTimeZoneName}
      />

      <div className={styles.billingWrapper}>
        <CustomCheckbox
          label="Billable"
          isChecked={data.isBillable}
          handleChange={toggleBillable}
        />
      </div>
      <TextArea
        label="Comments"
        value={data.notes}
        onChange={handleNotesChange}
        error={data.commentsLengthError}
        errorMessage={`*Maximum length ${MaximumLargeCommentLengthValidationError} characters`}
        style={{resize:"vertical"}}
      />
      <div className={styles.controlWrapper}>
        <Button onClick={handleClose}>Cancel</Button>

        {isJiraLoading ? (
          <div className={styles.JiraLoader}><PulseLoader/></div>
        ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={disableSubmitCondition}
            >
              Send
            </Button>
          )
        }
      </div>
    </div>
  );
};

export default AddBulkTimeForm;
